<script setup>
import { ref, computed, onMounted, watch, nextTick, useId } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import PipelineHeader from './PipelineHeader.vue';
import PipelineFilter from './PipelineFilter.vue';
import { usePipelinePermissions } from '../composables/usePipelinePermissions';
import PipelineCardsAPI from 'dashboard/api/pipelineCards';
import Modal from '../../../../components/Modal.vue';
import PipelineCardForm from './PipelineCardForm.vue';
import PipelineCardDetails from './PipelineCardDetails.vue';
import AgentAPI from '../../../../api/agents';
import CalendarMonth from '../../../../components/ui/DatePicker/components/CalendarMonth.vue';
import CalendarYear from '../../../../components/ui/DatePicker/components/CalendarYear.vue';
import CalendarWeekLabel from '../../../../components/ui/DatePicker/components/CalendarWeekLabel.vue';
import { CALENDAR_PERIODS } from '../../../../components/ui/DatePicker/helpers/DatePickerHelper';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import { useAlert } from 'dashboard/composables';
import {
  getCardPriority,
  matchesCardSearch,
  matchesColumnFilters,
  resolveCardAgent,
} from '../helpers/columnFilterMatchers';
import {
  formatInReportingTimezone,
  formatLocalizedDateInTimezone,
  resolveReportingTimezone,
  zonedDateTimeToUtc,
} from '../utils/localeFormatting';
import { buildSchedulingDetails } from '../helpers/agendaScheduling';
import { getCalendarDateForKey } from '../helpers/calendarNavigation';
import { orderedStagesForPipeline } from '../helpers/pipelineStageResolver';
import { PIPELINE_PAGINATION_ERROR_CODE } from '../helpers/pipelinePagination';
import { getStageColorClasses, stageColorStyle } from './pipelineTemplates';

const props = defineProps({
  currentView: {
    type: String,
    default: 'agenda',
  },
  columns: {
    type: Array,
    default: () => [],
  },
  pipelineCards: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});
const emit = defineEmits(['switchView']);
const { t, locale } = useI18n();

// Helper to convert locale format from pt_BR to pt-BR for Intl.DateTimeFormat
const getUserLocale = () => {
  return (locale.value || navigator.language || 'en-US').replace('_', '-');
};
const store = useStore();
const agendaSelectedPipeline = computed(
  () => store.getters['pipelines/getSelectedPipeline']
);
const { canViewPipeline } = usePipelinePermissions(agendaSelectedPipeline);
const items = ref([]);
const agents = ref({});
const searchQuery = ref('');
const activeFilters = computed(
  () => store.getters['pipelineCards/getActiveFilters'] || {}
);
const agendaLoadError = ref(null);
const showFilterModal = ref(false);
const currentMonth = ref(new Date());
const selectedDate = ref(new Date());
const showAddModal = ref(false);
const addCardFormRef = ref(null);
const viewMode = ref('month'); // month, week, day
const agendaViewModes = ['month', 'week'];
const agendaViewUid = useId();
const agendaViewTabId = mode => `${agendaViewUid}-agenda-${mode}-tab`;
const agendaViewPanelId = mode => `${agendaViewUid}-agenda-${mode}-panel`;

const setAgendaViewMode = (mode, { focusTab = false } = {}) => {
  if (!agendaViewModes.includes(mode)) return;
  viewMode.value = mode;
  if (focusTab) {
    nextTick(() => document.getElementById(agendaViewTabId(mode))?.focus());
  }
};

const handleAgendaViewTabKeydown = (event, currentMode) => {
  const currentIndex = agendaViewModes.indexOf(currentMode);
  if (currentIndex < 0) return;

  let nextIndex = null;
  if (['ArrowRight', 'ArrowDown'].includes(event.key)) {
    nextIndex = (currentIndex + 1) % agendaViewModes.length;
  } else if (['ArrowLeft', 'ArrowUp'].includes(event.key)) {
    nextIndex =
      (currentIndex - 1 + agendaViewModes.length) % agendaViewModes.length;
  } else if (event.key === 'Home') {
    nextIndex = 0;
  } else if (event.key === 'End') {
    nextIndex = agendaViewModes.length - 1;
  }

  if (nextIndex === null) return;
  event.preventDefault();
  setAgendaViewMode(agendaViewModes[nextIndex], { focusTab: true });
};

// Novas refs para o controle do mini calendário
const miniCalendarView = ref('days'); // 'days', 'months', 'years'
const { MONTH: PERIOD_MONTH, YEAR: PERIOD_YEAR } = CALENDAR_PERIODS;

// Refs para controle do modal de detalhes
const showItemDetails = ref(false);
const selectedItem = ref(null);

// Adicionar ref para controlar o estado de drag & drop
const dragOverCell = ref(null);

// Adicionar ref para controlar o estado de expansão
const isRecentItemsExpanded = ref(true);

// Adicionar nova ref para controlar a data selecionada no mini calendário
const selectedMiniDate = ref(new Date());

const showEditModal = ref(false);
const selectedItemToEdit = ref(null);
const editCardFormRef = ref(null);

// Refs para modal de seleção de hora
const showTimePickerModal = ref(false);
const pendingSchedule = ref(null); // { itemId, date, isReschedule }
const selectedTime = ref('09:00');
const isScheduling = ref(false);
const calendarGridRef = ref(null);

// Refs para busca em itens recentes
const recentItemsSearchQuery = ref('');
const showRecentSearch = ref(false);

// Refs para modal de agendamento por clique no dia
const showScheduleModal = ref(false);
const scheduleModalDate = ref(null);
const scheduleModalSearchQuery = ref('');
const selectedItemToSchedule = ref(null);

const reportingTimezone = computed(() => resolveReportingTimezone(store));
const agendaDefaultStage = computed(() => {
  const pipeline = agendaSelectedPipeline.value;
  const configuredStage = pipeline?.default_stage;
  if (configuredStage && pipeline?.stages?.[configuredStage]) {
    return configuredStage;
  }
  return orderedStagesForPipeline(pipeline)[0]?.id || '';
});
const loadError = computed(() => {
  const value = agendaLoadError.value || store.state.pipelineCards.error;
  return value === PIPELINE_PAGINATION_ERROR_CODE
    ? t('PIPELINE.DASHBOARD.PAGINATION_ERROR')
    : value;
});
const cardsMeta = computed(() => store.getters['pipelineCards/getMeta'] || {});
const isAppendingFromServer = computed(
  () => store.state.pipelineCards.appending
);
const hasMoreFromServer = computed(
  () =>
    store.state.pipelineCards.lastFetchedMode === 'global' &&
    Boolean(cardsMeta.value.has_more) &&
    Boolean(cardsMeta.value.next_cursor)
);
const hasPartialDataset = computed(
  () =>
    store.state.pipelineCards.lastFetchedMode === 'global' &&
    Boolean(cardsMeta.value.has_more)
);
const serverTotalCount = computed(() => {
  const stageTotal = Object.values(cardsMeta.value.stage_counts || {}).reduce(
    (total, count) => total + (Number(count) || 0),
    0
  );
  return Math.max(stageTotal, props.pipelineCards.length);
});

// Helper para formatar data local sem conversão UTC
const toLocalDateString = date => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const getReportingToday = () => {
  const dateKey =
    formatInReportingTimezone(
      new Date(),
      'yyyy-MM-dd',
      reportingTimezone.value
    ) || formatInReportingTimezone(new Date(), 'yyyy-MM-dd', 'UTC');
  const [year, month, day] = dateKey.split('-').map(Number);
  return new Date(year, month - 1, day, 12);
};

const goToToday = () => {
  const today = getReportingToday();
  selectedDate.value = today;
  selectedMiniDate.value = today;
  currentMonth.value = today;
};

const schedulingDate = item =>
  item?.item_details?.scheduled_at || item?.item_details?.deadline_at;

const reportingDateKey = value => {
  if (!value) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(value))) return String(value);
  return formatInReportingTimezone(
    value,
    'yyyy-MM-dd',
    reportingTimezone.value
  );
};

const filteredItems = computed(() =>
  items.value.filter(
    item =>
      matchesCardSearch(item.raw || item, searchQuery.value, item.stageName) &&
      matchesColumnFilters(item.raw || item, activeFilters.value, {
        reportingTimezone: reportingTimezone.value,
      })
  )
);

// Atualizar a função getEventsForDate para usar o horário local
const getEventsForDate = date => {
  return filteredItems.value.filter(item => {
    const itemDate = schedulingDate(item);
    if (!itemDate) return false;
    return reportingDateKey(itemDate) === toLocalDateString(date);
  });
};

// Computed para dias do mês atual
const daysInMonth = computed(() => {
  const year = currentMonth.value.getFullYear();
  const month = currentMonth.value.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const days = [];

  // Adicionar dias do mês anterior para completar a primeira semana
  const firstDayOfWeek = firstDay.getDay();
  for (let i = firstDayOfWeek - 1; i >= 0; i -= 1) {
    const date = new Date(year, month, -i);
    days.push({
      date,
      isCurrentMonth: false,
      events: getEventsForDate(date),
    });
  }

  // Adicionar dias do mês atual
  for (let date = 1; date <= lastDay.getDate(); date += 1) {
    const currentDate = new Date(year, month, date);
    days.push({
      date: currentDate,
      isCurrentMonth: true,
      events: getEventsForDate(currentDate),
    });
  }

  // Adicionar dias do próximo mês para completar a última semana
  const remainingDays = 42 - days.length; // 6 semanas * 7 dias = 42
  for (let i = 1; i <= remainingDays; i += 1) {
    const date = new Date(year, month + 1, i);
    days.push({
      date,
      isCurrentMonth: false,
      events: getEventsForDate(date),
    });
  }

  return days;
});

// Função para obter os dias da semana atual
const getWeekDays = () => {
  const today = selectedDate.value || new Date();
  const start = new Date(today);
  start.setDate(start.getDate() - start.getDay()); // Começa no domingo

  const days = [];
  for (let i = 0; i < 7; i += 1) {
    const date = new Date(start);
    date.setDate(start.getDate() + i);
    days.push({
      date,
      isCurrentMonth: date.getMonth() === currentMonth.value.getMonth(),
      events: getEventsForDate(date),
    });
  }
  return days;
};

// Adicionar computed para dias baseado no modo de visualização
const visibleDays = computed(() => {
  if (viewMode.value === 'week') {
    return getWeekDays();
  }
  return daysInMonth.value;
});

const selectedDayEvents = computed(() =>
  getEventsForDate(selectedDate.value || new Date())
);

const changeSelectedDay = direction => {
  const nextDate = new Date(selectedDate.value || new Date());
  nextDate.setDate(nextDate.getDate() + direction);
  selectedDate.value = nextDate;
  selectedMiniDate.value = new Date(nextDate);
  currentMonth.value = new Date(nextDate);
};

const selectMiniDate = date => {
  selectedMiniDate.value = new Date(date);
  selectedDate.value = new Date(date);
  currentMonth.value = new Date(date);
};

const showEventsForDay = date => {
  selectMiniDate(date);
  setAgendaViewMode('week');
};

// Atualizar o computed weeks para usar visibleDays
const weeks = computed(() => {
  const days =
    viewMode.value === 'week' ? visibleDays.value : daysInMonth.value;
  const result = [];
  for (let i = 0; i < days.length; i += 7) {
    result.push(days.slice(i, i + 7));
  }
  return result;
});

// Computed para nomes dos dias da semana
const weekDays = computed(() => {
  return [
    t('PIPELINE.AGENDA.WEEK_DAYS.SUN'),
    t('PIPELINE.AGENDA.WEEK_DAYS.MON'),
    t('PIPELINE.AGENDA.WEEK_DAYS.TUE'),
    t('PIPELINE.AGENDA.WEEK_DAYS.WED'),
    t('PIPELINE.AGENDA.WEEK_DAYS.THU'),
    t('PIPELINE.AGENDA.WEEK_DAYS.FRI'),
    t('PIPELINE.AGENDA.WEEK_DAYS.SAT'),
  ];
});

// Adicionar função para navegar entre semanas
const changeWeek = direction => {
  const newDate = new Date(selectedDate.value);
  newDate.setDate(newDate.getDate() + direction * 7);
  selectedDate.value = newDate;
  currentMonth.value = new Date(newDate); // Atualiza o mês se necessário
};

// Atualizar a função de mudança de mês para considerar o modo
const changeMonth = direction => {
  if (viewMode.value === 'week') {
    changeWeek(direction * 4); // Aproximadamente um mês
    return;
  }

  const newMonth = new Date(currentMonth.value);
  newMonth.setMonth(newMonth.getMonth() + direction);
  currentMonth.value = newMonth;
  selectedDate.value = newMonth;
};

// Format date using user's locale
const formatDate = (date, options = { month: 'long', year: 'numeric' }) => {
  return new Intl.DateTimeFormat(getUserLocale(), {
    ...options,
  }).format(date);
};

// Format an instant in the account timezone while respecting the UI locale.
const formatShortDate = dateString => {
  return (
    formatLocalizedDateInTimezone(dateString, {
      locale: getUserLocale(),
      timeZone: reportingTimezone.value,
    }) || t('PIPELINE.LIST.EMPTY_CELL')
  );
};
const pendingScheduleDateLabel = computed(() => {
  if (!pendingSchedule.value?.date) return '';
  return formatLocalizedDateInTimezone(
    toLocalDateString(pendingSchedule.value.date),
    {
      locale: getUserLocale(),
      timeZone: reportingTimezone.value,
      dateStyle: 'full',
    }
  );
});

// Função para verificar se é hoje
const isToday = date => {
  return (
    toLocalDateString(date) ===
    formatInReportingTimezone(new Date(), 'yyyy-MM-dd', reportingTimezone.value)
  );
};

// Formatar hora do evento (se existir)
// Formatar hora do evento (se existir) no fuso local do usuário.
// Strings ISO com 'Z' ou offset são UTC e precisam ser convertidas para
// o horário local do browser antes de exibir — caso contrário usuários
// em BRT (UTC-3) veriam 3h a mais (ex: salvou 09:00 BRT, exibia 12:00).
const formatEventTime = dateStr => {
  if (!dateStr) return '';
  // Se é apenas data (YYYY-MM-DD), não mostrar hora
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return '';

  const formatted = formatInReportingTimezone(
    dateStr,
    'HH:mm',
    reportingTimezone.value
  );
  return formatted === '00:00' ? '' : formatted;
};

// Atualizar o computed recentItems
const recentItems = computed(() => {
  // Pegar os 10 itens mais recentes, independente de terem ou não agendamento
  return filteredItems.value
    .filter(item => {
      // Filtrar apenas itens que não têm prazo/agendamento
      const hasScheduling =
        item.item_details?.scheduled_at || item.item_details?.deadline_at;
      return !hasScheduling;
    })
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 10);
});

// Função para carregar agentes (chamada uma vez)
const loadAgents = async () => {
  if (Object.keys(agents.value).length === 0) {
    try {
      const { data } = await AgentAPI.get();
      data.forEach(agent => {
        agents.value[agent.id] = agent;
      });
    } catch (e) {
      // Error handled silently
    }
  }
};

// Função para processar os cards do pipeline (usando a prop)
const processCards = cardsList => {
  const selectedPipeline = store.getters['pipelines/getSelectedPipeline'];
  const stages = selectedPipeline?.stages || {};

  return cardsList.map(item => {
    const agent = resolveCardAgent(item, agents.value);
    const stage = stages[item.pipeline_stage];

    return {
      id: item.id,
      title: item.title || item.item_details?.title || '',
      description: item.description || item.item_details?.description || '',
      priority: getCardPriority(item),
      pipeline_id: item.pipeline_id,
      pipeline_stage: item.pipeline_stage,
      position: item.position,
      item_details: {
        ...item.item_details,
        _agent: agent,
      },
      stageName: stage?.name || item.pipeline_stage || '',
      stageColor: stage?.color || '#64748B',
      created_at: item.created_at,
      custom_attributes: item.custom_attributes || {},
      raw: item,
    };
  });
};

const filteredResults = computed(() => {
  const stages = {};
  filteredItems.value.forEach(item => {
    stages[item.stageName] = (stages[item.stageName] || 0) + 1;
  });
  return { total: filteredItems.value.length, stages };
});

// Atualizar items quando pipelineCards mudar
const updateItemsFromProps = () => {
  items.value = processCards(props.pipelineCards);
};

// Função para forçar recarregamento via store (usada após edições)
const fetchPipelineCards = async () => {
  await store.dispatch('pipelineCards/fetchPipelineCardItems');
};

const handleLoadMoreFromServer = async () => {
  if (isAppendingFromServer.value) return;
  try {
    await store.dispatch('pipelineCards/fetchPipelineCardItems', {
      append: true,
    });
  } catch {
    // O store preserva o cursor e o botão para uma nova tentativa.
  }
};

// Handler para abrir o modal de agendamento ao clicar em um dia
const handleDayClickForSchedule = date => {
  scheduleModalDate.value = date;
  scheduleModalSearchQuery.value = '';
  selectedItemToSchedule.value = null;
  showScheduleModal.value = true;
};

// Handlers
const handleViewChange = view => {
  emit('switchView', view);
};

const handleDateClick = (date, events) => {
  selectedDate.value = date;
  // Abrir modal de agendamento em vez do modal de adicionar
  handleDayClickForSchedule(date, events);
};

const focusCalendarDate = date => {
  nextTick(() => {
    calendarGridRef.value
      ?.querySelector(`[data-calendar-date="${toLocalDateString(date)}"]`)
      ?.focus({ preventScroll: true });
  });
};

const handleCalendarCellKeydown = (event, day) => {
  const targetDate = getCalendarDateForKey(day.date, event.key);
  if (!targetDate) return;

  event.preventDefault();
  selectedDate.value = targetDate;
  if (
    viewMode.value === 'month' &&
    (targetDate.getMonth() !== currentMonth.value.getMonth() ||
      targetDate.getFullYear() !== currentMonth.value.getFullYear())
  ) {
    currentMonth.value = new Date(
      targetDate.getFullYear(),
      targetDate.getMonth(),
      1
    );
  }
  focusCalendarDate(targetDate);
};

const retryAgendaLoad = async () => {
  agendaLoadError.value = null;
  try {
    await store.dispatch('pipelineCards/fetchPipelineCardItems');
  } catch (error) {
    const value = store.state.pipelineCards.error || error?.code;
    agendaLoadError.value =
      value === PIPELINE_PAGINATION_ERROR_CODE
        ? t('PIPELINE.DASHBOARD.PAGINATION_ERROR')
        : error?.message || t('PIPELINE.ERRORS.FETCH_ITEMS');
  }
};

const handleFilter = () => {
  showFilterModal.value = true;
};

const applyFilters = async filters => {
  showFilterModal.value = false;
  agendaLoadError.value = null;
  try {
    await store.dispatch('pipelineCards/setGlobalFilters', filters || {});
  } catch (error) {
    agendaLoadError.value =
      store.state.pipelineCards.error ||
      error?.message ||
      t('PIPELINE.ERRORS.FETCH_ITEMS');
  }
};

const handleSearch = query => {
  searchQuery.value = query;
};

const handleItemCreated = async item => {
  if (!item) return;
  showAddModal.value = false;
  await fetchPipelineCards();
};

const requestAddModalClose = () =>
  addCardFormRef.value?.requestClose?.() ?? true;

const requestEditModalClose = () =>
  editCardFormRef.value?.requestClose?.() ?? true;

// Resolve o card COMPLETO (store) a partir do id. O evento do calendário é um
// snapshot reduzido (processCards remove won_at/lost_at/status/owner...), então
// para o detail abrir com dados corretos passamos o card cru.
const findRawCard = id =>
  store.getters['pipelineCards/getPipelineCardItems']?.find(c => c.id === id) ||
  props.pipelineCards.find(c => c.id === id);

// Função para abrir detalhes do item
const handleEventClick = event => {
  // Passa o card completo (não o snapshot do calendário) para o PipelineCardDetails
  // ter won_at/lost_at/status e refletir corretamente ganho/perdido.
  selectedItem.value = findRawCard(event.id) || event;
  showItemDetails.value = true;
};

// Após ganho/perdido/edição no detail: o modal aberto precisa refletir o novo
// estado na hora. O refetch da lista (fetchPipelineCardItems) tem cache de 5min
// e NÃO aceita force, então buscamos o card individual fresco via getCard e o
// commitamos no store — isso atualiza tanto o modal (selectedItem) quanto o
// calendário (props.pipelineCards vem do store). Sem isto, marcava Perdido/Ganho
// mas o modal seguia mostrando o estado anterior ("Aberto").
const handleDetailItemUpdated = async () => {
  const currentId = selectedItem.value?.id;
  if (!currentId) {
    await fetchPipelineCards();
    return;
  }
  try {
    const { data } = await PipelineCardsAPI.getCard(currentId);
    if (data) {
      selectedItem.value = data;
      store.commit('pipelineCards/UPDATE_PIPELINE_CARD_ITEM', data);
    }
  } catch (e) {
    // Fallback: tenta o estado do store/lista.
    await fetchPipelineCards();
    const updated = findRawCard(currentId);
    if (updated) selectedItem.value = updated;
  }
};

// Função para fechar detalhes do item
const handleCloseDetails = () => {
  showItemDetails.value = false;
  selectedItem.value = null;
};

// Função para abrir o modal de seleção de hora
const handleCardDrop = (itemId, date, isReschedule = false) => {
  pendingSchedule.value = { itemId, date, isReschedule };
  selectedTime.value = '09:00'; // Reset para hora padrão
  showTimePickerModal.value = true;
};

// Função para confirmar o agendamento com hora
const confirmScheduleWithTime = async () => {
  if (!pendingSchedule.value || isScheduling.value) return;

  isScheduling.value = true;
  try {
    const { itemId, date, isReschedule } = pendingSchedule.value;
    const item = items.value.find(i => i.id === itemId);
    if (!item) throw new Error('pipeline-card-not-found');

    const sourceDetails = item.raw?.item_details || item.item_details || {};

    const scheduledDate = zonedDateTimeToUtc(
      toLocalDateString(date),
      selectedTime.value,
      reportingTimezone.value
    );
    if (!scheduledDate) throw new Error('invalid-schedule-date');
    const scheduledAt = scheduledDate.toISOString();
    const updatedItemDetails = buildSchedulingDetails({
      sourceDetails,
      scheduledAt,
      isReschedule,
    });

    const payload = {
      pipeline_id: item.pipeline_id,
      pipeline_stage: item.pipeline_stage,
      position: item.position,
      item_details: updatedItemDetails,
      custom_attributes: item.custom_attributes || {},
    };

    const response = await PipelineCardsAPI.updateCard(itemId, payload);

    // Atualizar o item localmente no store para refletir a mudança imediatamente
    if (response.data) {
      await store.dispatch('pipelineCards/replaceCard', response.data);
    }
    showTimePickerModal.value = false;
    pendingSchedule.value = null;
  } catch {
    useAlert(t('PIPELINE.AGENDA.SCHEDULE_ERROR'));
  } finally {
    isScheduling.value = false;
  }
};

// Função para cancelar o agendamento
const cancelSchedule = () => {
  if (isScheduling.value) return;
  showTimePickerModal.value = false;
  pendingSchedule.value = null;
};

// Atualizar as funções de drag & drop
const allowDrop = (e, date) => {
  e.preventDefault();
  dragOverCell.value = date;
};

const handleDragLeave = () => {
  dragOverCell.value = null;
};

// Função para iniciar drag de eventos já agendados no calendário
const handleEventDragStart = (e, event) => {
  e.dataTransfer.setData('itemId', event.id);
  e.dataTransfer.setData('isReschedule', 'true');
  e.dataTransfer.effectAllowed = 'move';
  // Adicionar classe visual durante o drag
  e.target.classList.add('opacity-50', 'scale-95');
};

// Função para limpar visual após drag terminar
const handleEventDragEnd = e => {
  e.target.classList.remove('opacity-50', 'scale-95');
};

const handleDrop = async (e, date) => {
  e.preventDefault();
  const itemId = parseInt(e.dataTransfer.getData('itemId'), 10);
  const isReschedule = e.dataTransfer.getData('isReschedule') === 'true';
  if (!itemId) return;

  dragOverCell.value = null;
  await handleCardDrop(itemId, date, isReschedule);
};

// Adicionar função para alternar expansão
const toggleRecentItems = () => {
  isRecentItemsExpanded.value = !isRecentItemsExpanded.value;
};

const handleRecentSearch = () => {
  showRecentSearch.value = !showRecentSearch.value;
  if (showRecentSearch.value) {
    nextTick(() => {
      document.getElementById('recent-items-search')?.focus();
    });
  } else {
    recentItemsSearchQuery.value = '';
  }
};

// Adicione esta função para lidar com a edição
const handleItemEdit = item => {
  selectedItemToEdit.value = {
    ...item,
    pipeline_stage:
      item.pipeline_stage ||
      store.getters['pipelines/getSelectedPipeline']?.default_stage,
  };
  showEditModal.value = true;
};

// Adicione esta função para lidar com o item atualizado
const handleItemUpdated = async () => {
  showEditModal.value = false;
  selectedItemToEdit.value = null;
  showItemDetails.value = false;
  await fetchPipelineCards();
};

// Lifecycle hooks
onMounted(async () => {
  goToToday();
  await loadAgents();
  updateItemsFromProps();
});

// Watch para atualizar quando pipelineCards prop mudar
watch(
  () => props.pipelineCards,
  () => {
    updateItemsFromProps();
  },
  { deep: true, immediate: true }
);

// Watch para atualizar quando o funil mudar (reprocessar nomes dos estágios)
watch(
  () => store.getters['pipelines/getSelectedPipeline'],
  () => {
    updateItemsFromProps();
  }
);

// Computed para eventos do mês atual
const currentMonthEvents = computed(() => {
  const yearMonth = `${currentMonth.value.getFullYear()}-${String(
    currentMonth.value.getMonth() + 1
  ).padStart(2, '0')}`;
  return filteredItems.value.filter(item => {
    const itemDate = schedulingDate(item);
    if (!itemDate) return false;
    return reportingDateKey(itemDate).startsWith(yearMonth);
  });
});

const scheduledEvents = computed(() =>
  filteredItems.value.filter(item => Boolean(schedulingDate(item)))
);

// 6.1 — Heatmap da agenda: contagem de items agendados por dia do mês atual.
// Conta pela PARTE ISO da data (split('T')[0]) — exatamente como getEventsForDate
// casa card↔célula — para o heatmap não discordar dos eventos visíveis quando o
// deadline está em UTC-midnight (drift UTC/local).
const dayCountMap = computed(() => {
  const counts = {};
  const ym = `${currentMonth.value.getFullYear()}-${String(
    currentMonth.value.getMonth() + 1
  ).padStart(2, '0')}`;
  filteredItems.value.forEach(item => {
    const itemDate = schedulingDate(item);
    if (!itemDate) return;
    const datePart = reportingDateKey(itemDate);
    if (!datePart.startsWith(ym)) return;
    const day = Number(datePart.split('-')[2]);
    if (day) counts[day] = (counts[day] || 0) + 1;
  });
  return counts;
});

const maxDayCount = computed(() =>
  Math.max(0, ...Object.values(dayCountMap.value))
);

// Dia mais movimentado / mais tranquilo (entre os dias COM items).
const busiestDay = computed(() => {
  let result = null;
  Object.entries(dayCountMap.value).forEach(([day, count]) => {
    if (!result || count > result.count) result = { day: Number(day), count };
  });
  return result;
});

const quietestDay = computed(() => {
  let result = null;
  Object.entries(dayCountMap.value).forEach(([day, count]) => {
    if (!result || count < result.count) result = { day: Number(day), count };
  });
  return result;
});

// Intensidade da barra de heatmap por dia (4 níveis relativos ao pico do mês).
// Tokens n-blue-* (o tema redefine a paleta default — só n-* resolvem).
const heatmapBarClass = day => {
  const count = dayCountMap.value[day.date.getDate()] || 0;
  if (count === 0 || !day.isCurrentMonth) return null;
  const ratio = maxDayCount.value > 0 ? count / maxDayCount.value : 0;
  if (ratio > 0.75) return 'bg-n-blue-10';
  if (ratio > 0.5) return 'bg-n-blue-8';
  if (ratio > 0.25) return 'bg-n-blue-6';
  return 'bg-n-blue-4';
};

const dayCountFor = day =>
  day.isCurrentMonth ? dayCountMap.value[day.date.getDate()] || 0 : 0;

// Computed para eventos remarcados
const rescheduledEvents = computed(() => {
  return filteredItems.value.filter(item => item.item_details?.rescheduled);
});

// Computed para eventos pendentes (sem data definida)
const pendingEvents = computed(() => {
  return filteredItems.value.filter(item => {
    return !item.item_details?.scheduled_at && !item.item_details?.deadline_at;
  });
});

// Função auxiliar para comparar se duas datas são o mesmo dia
const isSameDay = (date1, date2) => {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
};

// Handlers para os novos componentes do mini calendário
const openMiniCalendarMonthView = () => {
  miniCalendarView.value = 'months';
};

const handleMiniCalendarSetView = (calendarType, period) => {
  if (period === PERIOD_YEAR) {
    miniCalendarView.value = 'years';
  } else if (period === PERIOD_MONTH) {
    miniCalendarView.value = 'months';
  } else {
    miniCalendarView.value = 'days';
  }
};

const handleMiniCalendarSelectMonth = monthIndex => {
  const newDate = new Date(currentMonth.value);
  newDate.setMonth(monthIndex);
  currentMonth.value = newDate;
  selectedMiniDate.value = new Date(newDate); // Sincronizar selectedMiniDate
  miniCalendarView.value = 'days';
};

const handleMiniCalendarSelectYear = year => {
  const newDate = new Date(currentMonth.value);
  newDate.setFullYear(year);
  currentMonth.value = newDate;
  selectedMiniDate.value = new Date(newDate); // Sincronizar selectedMiniDate
  miniCalendarView.value = 'months'; // Mudar para visualização de meses após selecionar o ano
};

const handleMiniCalendarPrev = () => {
  // Usado por CalendarMonth para navegar para o ano anterior
  const newDate = new Date(currentMonth.value);
  newDate.setFullYear(newDate.getFullYear() - 1);
  currentMonth.value = newDate;
  selectedMiniDate.value = new Date(newDate);
};

const handleMiniCalendarNext = () => {
  // Usado por CalendarMonth para navegar para o próximo ano
  const newDate = new Date(currentMonth.value);
  newDate.setFullYear(newDate.getFullYear() + 1);
  currentMonth.value = newDate;
  selectedMiniDate.value = new Date(newDate);
};

// Computed para itens recentes filtrados (com busca)
const filteredRecentItems = computed(() => {
  const query = recentItemsSearchQuery.value.toLowerCase().trim();
  if (!query) return recentItems.value;

  return recentItems.value.filter(item =>
    matchesCardSearch(item.raw || item, query, item.stageName)
  );
});

// Computed para todos os itens sem data (para o modal de agendamento)
const allUnscheduledItems = computed(() => {
  return filteredItems.value
    .filter(item => {
      const hasScheduling =
        item.item_details?.scheduled_at || item.item_details?.deadline_at;
      return !hasScheduling;
    })
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
});

// Computed para itens filtrados no modal de agendamento
const filteredScheduleModalItems = computed(() => {
  const query = scheduleModalSearchQuery.value.toLowerCase().trim();
  if (!query) return allUnscheduledItems.value.slice(0, 20); // Mostrar os 20 mais recentes

  return allUnscheduledItems.value.filter(item =>
    matchesCardSearch(item.raw || item, query, item.stageName)
  );
});

// Computed para verificar se um evento está atrasado
const isOverdue = item => {
  const deadline = item.item_details?.deadline_at;
  if (!deadline) return false;
  const deadlineDate = new Date(deadline);
  const now = new Date();
  return deadlineDate < now;
};

const PRIORITY_ACCENT_CLASSES = {
  urgent: 'border-l-n-ruby-9',
  high: 'border-l-n-orange-9',
  medium: 'border-l-n-amber-9',
  low: 'border-l-n-green-9',
};

const PRIORITY_TEXT_CLASSES = {
  urgent: 'text-n-ruby-11',
  high: 'text-n-orange-11',
  medium: 'text-n-amber-11',
  low: 'text-n-green-11',
};

// Irmão de `getEventClasses`: cor de etapa fora das 5 conhecidas não tem classe.
// Atrasado tem cor semântica própria e ignora a cor da etapa, de propósito.
const getEventStyles = event => {
  if (isOverdue(event)) return undefined;
  const estilos = stageColorStyle(event.stageColor, 'badge');
  const acento = stageColorStyle(event.stageColor, 'accent');
  if (!estilos && !acento) return undefined;
  return {
    ...(estilos || {}),
    ...(PRIORITY_ACCENT_CLASSES[event.priority] ? {} : acento || {}),
  };
};

const getEventClasses = event => {
  if (isOverdue(event)) {
    return 'border-n-ruby-5 border-l-n-ruby-9 bg-n-ruby-3 text-n-ruby-11';
  }

  const stageClasses = getStageColorClasses(event.stageColor);
  return [
    stageClasses.badge,
    PRIORITY_ACCENT_CLASSES[event.priority] || stageClasses.accent,
  ];
};

const getPriorityTextClass = priority =>
  PRIORITY_TEXT_CLASSES[priority] || 'text-n-slate-10';

// Handler para selecionar um item no modal de agendamento
const selectItemForSchedule = item => {
  selectedItemToSchedule.value = item;
};

// Handler para confirmar o agendamento do modal
const confirmScheduleFromModal = () => {
  if (!selectedItemToSchedule.value || !scheduleModalDate.value) return;

  // Fechar o modal de seleção e abrir o de hora
  showScheduleModal.value = false;
  handleCardDrop(selectedItemToSchedule.value.id, scheduleModalDate.value);
};

// Handler para cancelar o modal de agendamento
const cancelScheduleModal = () => {
  showScheduleModal.value = false;
  scheduleModalDate.value = null;
  scheduleModalSearchQuery.value = '';
  selectedItemToSchedule.value = null;
};
</script>

<template>
  <div class="flex w-full flex-1 min-w-0 flex-col h-full bg-n-background">
    <!-- Access denied: agent is not a member of this pipeline -->
    <div
      v-if="!canViewPipeline"
      class="flex flex-1 items-center justify-center p-8"
    >
      <div class="text-center max-w-sm">
        <fluent-icon
          icon="lock-closed"
          size="40"
          class="mx-auto mb-4 text-n-slate-10"
        />
        <h3 class="text-heading-3 text-n-slate-12 mb-2">
          {{ t('PIPELINE.ACCESS_DENIED.TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-11">
          {{ t('PIPELINE.ACCESS_DENIED.DESCRIPTION') }}
        </p>
      </div>
    </div>

    <template v-else>
      <PipelineHeader
        :current-view="currentView"
        :search-results="filteredResults"
        :active-filters="activeFilters"
        :columns="columns"
        :pipeline-cards="items"
        :has-partial-dataset="hasPartialDataset"
        @switch-view="handleViewChange"
        @filter="handleFilter"
        @search="handleSearch"
      />

      <div
        v-if="hasMoreFromServer && !loadError"
        data-testid="agenda-server-pagination"
        class="flex flex-col items-center justify-between gap-2 border-b border-n-weak bg-n-solid-2 px-4 py-2 sm:flex-row sm:px-6"
        aria-live="polite"
      >
        <span class="text-xs text-n-slate-10">
          {{
            t('PIPELINE.LIST.SHOWING_OF', {
              loaded: pipelineCards.length,
              total: serverTotalCount,
            })
          }}
        </span>
        <Button
          variant="faded"
          color="slate"
          size="sm"
          class="min-h-11"
          data-testid="agenda-load-more"
          :is-loading="isAppendingFromServer"
          :disabled="isAppendingFromServer"
          @click="handleLoadMoreFromServer"
        >
          <template #icon>
            <fluent-icon icon="chevron-down" size="16" />
          </template>
          {{ t('PIPELINE.LOAD_MORE_SIMPLE') }}
        </Button>
      </div>

      <PipelineFilter
        :show="showFilterModal"
        :filtered-results="filteredResults"
        :current-pipeline="agendaSelectedPipeline"
        :initial-filters="activeFilters"
        :columns="columns"
        scope="global"
        @apply="applyFilters"
        @close="showFilterModal = false"
      />

      <div
        v-if="loadError && !isLoading"
        role="alert"
        class="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center text-n-ruby-11"
      >
        <p>{{ loadError }}</p>
        <button
          type="button"
          class="min-h-11 rounded-lg border border-n-weak px-4 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
          @click="retryAgendaLoad"
        >
          {{ t('PIPELINE.MESSAGES.RETRY') }}
        </button>
      </div>

      <div
        v-else-if="isLoading"
        class="grid flex-1 grid-cols-1 gap-3 overflow-hidden p-3 md:grid-cols-7"
        role="status"
        aria-live="polite"
        :aria-label="t('PIPELINE.LOADING_ITEMS')"
      >
        <div
          v-for="column in 7"
          :key="column"
          class="hidden min-h-64 animate-pulse rounded-xl border border-n-weak bg-n-alpha-1 p-3 motion-reduce:animate-none md:block"
        >
          <div class="mb-5 h-4 w-16 rounded bg-n-alpha-3" />
          <div class="mb-2 h-16 rounded-lg bg-n-alpha-2" />
          <div class="h-12 rounded-lg bg-n-alpha-2" />
        </div>
        <div
          class="min-h-64 animate-pulse rounded-xl border border-n-weak bg-n-alpha-1 p-4 motion-reduce:animate-none md:hidden"
        >
          <div class="mb-5 h-5 w-40 rounded bg-n-alpha-3" />
          <div class="mb-3 h-20 rounded-lg bg-n-alpha-2" />
          <div class="h-20 rounded-lg bg-n-alpha-2" />
        </div>
      </div>

      <div v-else class="flex flex-1 min-h-0 flex-col">
        <div class="flex flex-1 min-h-0">
          <!-- Mini Calendar Sidebar -->
          <div
            class="hidden lg:flex w-[336px] border-r border-n-weak flex-col overflow-hidden relative z-[3] h-full [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            <div class="p-3 flex-shrink-0 flex flex-col h-full">
              <!-- Mini Calendar Header -->
              <div
                v-if="miniCalendarView === 'days'"
                class="flex items-center justify-between mb-3"
              >
                <button
                  type="button"
                  class="min-h-11 rounded-lg px-2 text-xs font-semibold text-n-slate-12 hover:text-n-blue-11 capitalize transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
                  :aria-label="t('PIPELINE.AGENDA.CHOOSE_MONTH')"
                  @click="openMiniCalendarMonthView"
                >
                  {{ formatDate(currentMonth) }}
                </button>
                <div class="flex gap-2">
                  <button
                    type="button"
                    class="flex min-h-11 min-w-11 items-center justify-center rounded-md text-n-slate-10 transition-colors hover:bg-n-alpha-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
                    :aria-label="t('PIPELINE.AGENDA.PREVIOUS_MONTH')"
                    @click="changeMonth(-1)"
                  >
                    <Icon icon="i-lucide-chevron-left" class="size-3" />
                  </button>
                  <button
                    type="button"
                    class="flex min-h-11 min-w-11 items-center justify-center rounded-md text-n-slate-10 transition-colors hover:bg-n-alpha-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
                    :aria-label="t('PIPELINE.AGENDA.NEXT_MONTH')"
                    @click="changeMonth(1)"
                  >
                    <Icon icon="i-lucide-chevron-right" class="size-3" />
                  </button>
                </div>
              </div>

              <!-- CalendarMonth View -->
              <CalendarMonth
                v-if="miniCalendarView === 'months'"
                calendar-type="start"
                :start-current-date="currentMonth"
                :end-current-date="currentMonth"
                class="mb-4"
                @select-month="handleMiniCalendarSelectMonth"
                @set-view="handleMiniCalendarSetView"
                @prev="handleMiniCalendarPrev"
                @next="handleMiniCalendarNext"
              />

              <!-- CalendarYear View -->
              <CalendarYear
                v-if="miniCalendarView === 'years'"
                calendar-type="start"
                :start-current-date="currentMonth"
                :end-current-date="currentMonth"
                class="mb-4"
                @select-year="handleMiniCalendarSelectYear"
                @prev="handleMiniCalendarPrev"
                @next="handleMiniCalendarNext"
              />

              <!-- Mini Calendar Grid (apenas se a visualização for 'days') -->
              <div
                v-if="miniCalendarView === 'days'"
                class="mini-calendar mb-6 flex-shrink-0"
              >
                <!-- Mini Week Days - Substituído por CalendarWeekLabel -->
                <CalendarWeekLabel class="mb-2" />

                <!-- Mini Days Grid -->
                <div class="grid grid-cols-7 gap-0">
                  <button
                    v-for="day in daysInMonth"
                    :key="day.date"
                    type="button"
                    :aria-label="formatDate(day.date, { dateStyle: 'full' })"
                    class="relative flex size-11 items-center justify-center rounded-full text-xs cursor-pointer transition-colors focus-visible:outline-2 focus-visible:outline-n-brand"
                    :class="[
                      // Background: today > current month > other month
                      isToday(day.date)
                        ? 'bg-n-blue-9'
                        : day.isCurrentMonth
                          ? 'bg-n-solid-2 hover:bg-n-alpha-2'
                          : 'bg-n-alpha-1',
                      // Text color: today > current month > other month
                      isToday(day.date)
                        ? 'text-white font-medium'
                        : day.isCurrentMonth
                          ? 'text-n-slate-12'
                          : 'text-n-slate-9',
                      // Selected ring (not for today)
                      isSameDay(day.date, selectedMiniDate) &&
                      !isToday(day.date)
                        ? 'ring-2 ring-n-blue-9'
                        : '',
                    ]"
                    @click="selectMiniDate(day.date)"
                  >
                    {{ day.date.getDate() }}
                    <!-- Event indicator dot -->
                    <span
                      v-if="day.events.length > 0"
                      class="absolute bottom-0.5 left-1/2 -translate-x-1/2 size-1 rounded-full"
                      :class="isToday(day.date) ? 'bg-white' : 'bg-n-blue-9'"
                    />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Compact daily agenda for phones -->
          <div class="flex min-h-0 flex-1 flex-col md:hidden">
            <div
              class="flex items-center justify-between gap-2 border-b border-n-weak bg-n-solid-2 px-3 py-2"
            >
              <Button
                variant="ghost"
                color="slate"
                size="md"
                :aria-label="t('PIPELINE.AGENDA.PREVIOUS_DAY')"
                @click="changeSelectedDay(-1)"
              >
                <template #icon>
                  <Icon icon="i-lucide-chevron-left" class="size-5" />
                </template>
              </Button>
              <div class="min-w-0 text-center">
                <p class="truncate text-sm font-semibold text-n-slate-12">
                  {{
                    formatDate(selectedDate, {
                      weekday: 'long',
                      day: '2-digit',
                      month: 'long',
                    })
                  }}
                </p>
                <button
                  type="button"
                  class="min-h-11 px-2 text-xs font-medium text-n-brand focus-visible:outline-2 focus-visible:outline-n-brand"
                  @click="goToToday"
                >
                  {{ t('PIPELINE.AGENDA.VIEW_MODES.TODAY') }}
                </button>
              </div>
              <Button
                variant="ghost"
                color="slate"
                size="md"
                :aria-label="t('PIPELINE.AGENDA.NEXT_DAY')"
                @click="changeSelectedDay(1)"
              >
                <template #icon>
                  <Icon icon="i-lucide-chevron-right" class="size-5" />
                </template>
              </Button>
            </div>

            <div class="min-h-0 flex-1 space-y-2 overflow-y-auto p-3">
              <p
                v-if="selectedDayEvents.length === 0"
                role="status"
                class="py-10 text-center text-sm text-n-slate-10"
              >
                {{ t('PIPELINE.AGENDA.NO_EVENTS_TODAY') }}
              </p>
              <button
                v-for="event in selectedDayEvents"
                :key="event.id"
                type="button"
                class="flex min-h-14 w-full items-center gap-3 rounded-xl border border-n-weak bg-n-solid-2 p-3 text-left focus-visible:outline-2 focus-visible:outline-n-brand"
                @click="handleEventClick(event)"
              >
                <span
                  data-test="agenda-stage-color"
                  class="h-9 w-1 shrink-0 rounded-full"
                  :class="getStageColorClasses(event.stageColor).dot"
                  :style="stageColorStyle(event.stageColor, 'dot')"
                />
                <span class="min-w-0 flex-1">
                  <span
                    class="block truncate text-sm font-medium text-n-slate-12"
                  >
                    {{ event.title || t('PIPELINE.NO_TITLE') }}
                  </span>
                  <span class="block text-xs text-n-slate-10">
                    {{
                      formatEventTime(schedulingDate(event)) || event.stageName
                    }}
                  </span>
                </span>
                <Icon
                  icon="i-lucide-chevron-right"
                  class="size-4 text-n-slate-9"
                />
              </button>
            </div>

            <div class="border-t border-n-weak bg-n-solid-2 p-3">
              <Button
                variant="solid"
                color="blue"
                size="lg"
                class="w-full"
                @click="handleDayClickForSchedule(selectedDate)"
              >
                {{ t('PIPELINE.AGENDA.SCHEDULE_ITEM') }}
              </Button>
            </div>
          </div>

          <!-- Main Calendar -->
          <div class="hidden flex-1 flex-col min-h-0 md:flex">
            <!-- Main Calendar Header -->
            <div class="flex-shrink-0 bg-n-solid-2 z-10">
              <div class="flex items-center justify-between px-5 py-2.5">
                <h2 class="text-sm font-semibold text-n-slate-12 capitalize">
                  {{
                    formatDate(
                      viewMode === 'week' ? selectedDate : currentMonth
                    )
                  }}
                </h2>
                <div class="flex items-center gap-2">
                  <button
                    type="button"
                    class="min-h-11 px-3 py-2 text-xs font-medium text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-1 rounded-md focus-visible:outline-2 focus-visible:outline-n-brand transition-colors"
                    @click="goToToday"
                  >
                    {{ t('PIPELINE.AGENDA.VIEW_MODES.TODAY') }}
                  </button>
                  <div
                    role="tablist"
                    aria-orientation="horizontal"
                    :aria-label="t('PIPELINE.AGENDA.VIEW_MODE_LABEL')"
                    class="flex items-center rounded-lg bg-n-alpha-1 p-0.5"
                  >
                    <button
                      :id="agendaViewTabId('month')"
                      type="button"
                      role="tab"
                      class="min-h-11 px-3 py-2 text-xs font-medium rounded-md focus-visible:outline-2 focus-visible:outline-n-brand transition-colors duration-150"
                      :aria-selected="viewMode === 'month'"
                      :aria-controls="agendaViewPanelId('month')"
                      :tabindex="viewMode === 'month' ? 0 : -1"
                      :class="
                        viewMode === 'month'
                          ? 'bg-n-solid-2 shadow-sm text-n-slate-12'
                          : 'text-n-slate-10 hover:text-n-slate-12'
                      "
                      @click="setAgendaViewMode('month')"
                      @keydown="handleAgendaViewTabKeydown($event, 'month')"
                    >
                      {{ t('PIPELINE.AGENDA.VIEW_MODES.MONTH') }}
                    </button>
                    <button
                      :id="agendaViewTabId('week')"
                      type="button"
                      role="tab"
                      class="min-h-11 px-3 py-2 text-xs font-medium rounded-md focus-visible:outline-2 focus-visible:outline-n-brand transition-colors duration-150"
                      :aria-selected="viewMode === 'week'"
                      :aria-controls="agendaViewPanelId('week')"
                      :tabindex="viewMode === 'week' ? 0 : -1"
                      :class="
                        viewMode === 'week'
                          ? 'bg-n-solid-2 shadow-sm text-n-slate-12'
                          : 'text-n-slate-10 hover:text-n-slate-12'
                      "
                      @click="setAgendaViewMode('week')"
                      @keydown="handleAgendaViewTabKeydown($event, 'week')"
                    >
                      {{ t('PIPELINE.AGENDA.VIEW_MODES.WEEK') }}
                    </button>
                  </div>
                </div>
              </div>

              <!-- Week Days Header -->
              <div
                class="grid grid-cols-7 border-b border-t border-n-weak bg-n-alpha-1"
              >
                <div
                  v-for="day in weekDays"
                  :key="day"
                  class="py-2 text-[10px] font-semibold text-n-slate-10 text-center uppercase tracking-wider border-r last:border-r-0 border-n-weak"
                >
                  {{ day }}
                </div>
              </div>
            </div>

            <div
              :id="agendaViewPanelId(viewMode)"
              role="tabpanel"
              :aria-labelledby="agendaViewTabId(viewMode)"
              tabindex="0"
              class="flex min-h-0 flex-1 flex-col focus-visible:outline-2 focus-visible:outline-n-brand focus-visible:outline-offset-[-2px]"
            >
              <!-- Calendar Grid -->
              <div
                ref="calendarGridRef"
                role="grid"
                :aria-label="t('PIPELINE.AGENDA.CALENDAR_LABEL')"
                class="grid flex-1 min-h-0 border-l border-t border-n-weak relative isolate z-[1] overflow-y-auto"
                :class="{
                  'grid-rows-[repeat(6,1fr)]': viewMode === 'month',
                  'grid-rows-[1fr] h-full overflow-hidden border-none':
                    viewMode === 'week',
                }"
              >
                <div
                  v-for="week in weeks"
                  :key="week[0].date"
                  class="grid grid-cols-7"
                >
                  <div
                    v-for="day in week"
                    :key="day.date"
                    role="gridcell"
                    :tabindex="isSameDay(day.date, selectedDate) ? 0 : -1"
                    :aria-selected="isSameDay(day.date, selectedDate)"
                    :data-calendar-date="toLocalDateString(day.date)"
                    :aria-label="formatDate(day.date, { dateStyle: 'full' })"
                    class="relative p-2 border-r border-b border-t border-n-weak last:border-r-0 flex flex-col isolate hover:z-[1] hover:bg-n-alpha-1 focus-visible:z-[2] focus-visible:outline-2 focus-visible:outline-n-brand focus-visible:outline-offset-[-2px] transition-colors duration-150"
                    :class="{
                      'bg-n-alpha-1':
                        !day.isCurrentMonth &&
                        dragOverCell?.getTime?.() !== day.date.getTime(),
                      'bg-n-solid-2':
                        day.isCurrentMonth &&
                        dragOverCell?.getTime?.() !== day.date.getTime(),
                      today: isToday(day.date),
                      'h-full': viewMode === 'week',
                      'flex flex-col h-full': viewMode === 'week',
                      'bg-n-blue-3 ring-2 ring-n-blue-9 ring-inset':
                        dragOverCell?.getTime?.() === day.date.getTime(),
                    }"
                    @click="handleDateClick(day.date, day.events)"
                    @keydown.enter.self="handleDateClick(day.date, day.events)"
                    @keydown.space.self.prevent="
                      handleDateClick(day.date, day.events)
                    "
                    @keydown="handleCalendarCellKeydown($event, day)"
                    @dragover="e => allowDrop(e, day.date)"
                    @dragleave="handleDragLeave"
                    @drop="e => handleDrop(e, day.date)"
                  >
                    <!-- 6.1: barra de heatmap (intensidade = nº de items no dia) -->
                    <div
                      v-if="viewMode === 'month' && heatmapBarClass(day)"
                      class="absolute top-0 left-0 right-0 h-1 pointer-events-none"
                      :class="heatmapBarClass(day)"
                    />

                    <div class="h-full flex flex-col">
                      <span
                        v-tooltip="
                          viewMode === 'month' && dayCountFor(day) > 0
                            ? {
                                content: t('PIPELINE.AGENDA.DAY_ITEM_COUNT', {
                                  count: dayCountFor(day),
                                }),
                                delay: { show: 300, hide: 0 },
                                placement: 'top',
                              }
                            : undefined
                        "
                        class="text-sm mb-1"
                        :class="{
                          'text-n-slate-9': !day.isCurrentMonth,
                          'text-n-slate-12': day.isCurrentMonth,
                          'bg-n-blue-9 text-white rounded-full w-6 h-6 flex items-center justify-center':
                            isToday(day.date),
                        }"
                      >
                        {{ day.date.getDate() }}
                      </span>

                      <!-- Time-slotted events for week view -->
                      <template v-if="viewMode === 'week'">
                        <div
                          class="mt-1 relative flex-1 overflow-y-auto overflow-x-hidden min-h-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden space-y-1"
                        >
                          <button
                            v-for="event in day.events"
                            :key="event.id"
                            type="button"
                            class="min-h-11 w-full truncate rounded border border-l-[3px] p-1 text-left text-xs cursor-move hover:opacity-90 mb-1 focus-visible:outline-2 focus-visible:outline-n-brand transition-opacity duration-150"
                            :class="[
                              getEventClasses(event),
                              isOverdue(event)
                                ? 'ring-2 ring-n-ruby-9 ring-opacity-50'
                                : '',
                            ]"
                            :style="getEventStyles(event)"
                            :title="`${event.title}${event.priority && event.priority !== 'none' ? ' - ' + event.priority : ''}${isOverdue(event) ? ' (' + t('PIPELINE.CARD.OVERDUE') + ')' : ''} - ${t('PIPELINE.AGENDA.DRAG_TO_RESCHEDULE')}`"
                            draggable="true"
                            @dragstart="e => handleEventDragStart(e, event)"
                            @dragend="handleEventDragEnd"
                            @click.stop="handleEventClick(event)"
                          >
                            <div class="flex items-center gap-1">
                              <Icon
                                v-if="isOverdue(event)"
                                icon="i-lucide-alert-triangle"
                                class="size-3 text-n-ruby-9 flex-shrink-0"
                              />
                              <Icon
                                v-else
                                :icon="
                                  event.item_details?.scheduling_type ===
                                  'deadline'
                                    ? 'i-lucide-alarm-clock'
                                    : 'i-lucide-book-open'
                                "
                                class="size-3"
                              />
                              <span
                                v-if="
                                  formatEventTime(
                                    event.item_details?.deadline_at ||
                                      event.item_details?.scheduled_at
                                  )
                                "
                                class="font-medium flex-shrink-0"
                              >
                                {{
                                  formatEventTime(
                                    event.item_details?.deadline_at ||
                                      event.item_details?.scheduled_at
                                  )
                                }}
                              </span>
                              <span class="truncate">{{ event.title }}</span>
                            </div>
                          </button>
                        </div>
                      </template>

                      <!-- Regular events for month view -->
                      <template v-else>
                        <!-- Events -->
                        <div
                          class="mt-1 relative flex-1 overflow-y-auto overflow-x-hidden min-h-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden space-y-1"
                        >
                          <button
                            v-for="event in day.events.slice(0, 3)"
                            :key="event.id"
                            type="button"
                            class="min-h-11 w-full truncate rounded border border-l-[3px] p-1 text-left text-xs cursor-move hover:opacity-90 focus-visible:outline-2 focus-visible:outline-n-brand transition-opacity duration-150"
                            :class="[
                              getEventClasses(event),
                              isOverdue(event) ? 'ring-1 ring-n-ruby-9' : '',
                            ]"
                            :style="getEventStyles(event)"
                            :title="`${event.title}${event.priority && event.priority !== 'none' ? ' - ' + event.priority : ''}${isOverdue(event) ? ' (' + t('PIPELINE.CARD.OVERDUE') + ')' : ''} - ${t('PIPELINE.AGENDA.DRAG_TO_RESCHEDULE')}`"
                            draggable="true"
                            @dragstart="e => handleEventDragStart(e, event)"
                            @dragend="handleEventDragEnd"
                            @click.stop="handleEventClick(event)"
                          >
                            <div class="flex items-center gap-1">
                              <Icon
                                v-if="isOverdue(event)"
                                icon="i-lucide-alert-triangle"
                                class="size-3 text-n-ruby-9 flex-shrink-0"
                              />
                              <Icon
                                v-else
                                :icon="
                                  event.item_details?.scheduling_type ===
                                  'deadline'
                                    ? 'i-lucide-alarm-clock'
                                    : 'i-lucide-book-open'
                                "
                                class="size-3 flex-shrink-0"
                              />
                              <span
                                v-if="
                                  formatEventTime(
                                    event.item_details?.deadline_at ||
                                      event.item_details?.scheduled_at
                                  )
                                "
                                class="font-medium flex-shrink-0"
                              >
                                {{
                                  formatEventTime(
                                    event.item_details?.deadline_at ||
                                      event.item_details?.scheduled_at
                                  )
                                }}
                              </span>
                              <span class="truncate">{{ event.title }}</span>
                            </div>
                          </button>
                          <button
                            v-if="day.events.length > 3"
                            type="button"
                            class="min-h-11 w-full pl-1 text-left text-xs text-n-slate-10 rounded focus-visible:outline-2 focus-visible:outline-n-brand"
                            @click.stop="showEventsForDay(day.date)"
                          >
                            {{
                              t('PIPELINE.AGENDA.MORE_EVENTS', {
                                count: day.events.length - 3,
                              })
                            }}
                          </button>
                        </div>
                      </template>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Resumo do Mês -->
            <div
              class="flex-shrink-0 border-t border-n-weak bg-n-solid-2 relative z-[3]"
            >
              <div class="flex items-center gap-5 px-5 py-2">
                <!-- Total de Eventos -->
                <div class="flex items-center gap-1.5">
                  <Icon icon="i-lucide-layers" class="size-3 text-n-slate-10" />
                  <span class="text-[11px] text-n-slate-11">
                    {{ t('PIPELINE.AGENDA.TOTAL_EVENTS') }}
                  </span>
                  <span
                    class="text-[11px] font-semibold text-n-slate-12 tabular-nums"
                  >
                    {{ scheduledEvents.length }}
                  </span>
                </div>

                <div class="w-px h-3 bg-n-weak" />

                <!-- Eventos do Mês -->
                <div class="flex items-center gap-1.5">
                  <Icon
                    icon="i-lucide-calendar"
                    class="size-3 text-n-blue-10"
                  />
                  <span class="text-[11px] text-n-blue-11">
                    {{ t('PIPELINE.AGENDA.MONTH_EVENTS') }}
                  </span>
                  <span
                    class="text-[11px] font-semibold text-n-blue-11 tabular-nums"
                  >
                    {{ currentMonthEvents.length }}
                  </span>
                </div>

                <div class="w-px h-3 bg-n-weak" />

                <!-- Eventos Remarcados -->
                <div class="flex items-center gap-1.5">
                  <Icon
                    icon="i-lucide-refresh-cw"
                    class="size-3 text-n-slate-10"
                  />
                  <span class="text-[11px] text-n-slate-11">
                    {{ t('PIPELINE.AGENDA.RESCHEDULED_EVENTS') }}
                  </span>
                  <span
                    class="text-[11px] font-semibold text-n-slate-12 tabular-nums"
                  >
                    {{ rescheduledEvents.length }}
                  </span>
                </div>

                <div class="w-px h-3 bg-n-weak" />

                <!-- Eventos Pendentes -->
                <div class="flex items-center gap-1.5">
                  <Icon
                    icon="i-lucide-alert-circle"
                    class="size-3 text-n-ruby-10"
                  />
                  <span class="text-[11px] text-n-ruby-11">
                    {{ t('PIPELINE.AGENDA.NO_DATE_ITEMS') }}
                  </span>
                  <span
                    class="text-[11px] font-semibold text-n-ruby-11 tabular-nums"
                  >
                    {{ pendingEvents.length }}
                  </span>
                </div>

                <!-- 6.1: dia mais movimentado / mais tranquilo -->
                <template v-if="busiestDay">
                  <div class="w-px h-3 bg-n-weak" />
                  <div class="flex items-center gap-1.5">
                    <Icon
                      icon="i-lucide-trending-up"
                      class="size-3 text-n-blue-10"
                    />
                    <span class="text-[11px] text-n-slate-11">
                      {{ t('PIPELINE.AGENDA.BUSIEST_DAY') }}
                    </span>
                    <span
                      class="text-[11px] font-semibold text-n-blue-11 tabular-nums"
                    >
                      {{
                        t('PIPELINE.AGENDA.DAY_WITH_COUNT', {
                          day: busiestDay.day,
                          count: busiestDay.count,
                        })
                      }}
                    </span>
                  </div>
                </template>

                <template
                  v-if="quietestDay && quietestDay.day !== busiestDay?.day"
                >
                  <div class="w-px h-3 bg-n-weak" />
                  <div class="flex items-center gap-1.5">
                    <Icon
                      icon="i-lucide-trending-down"
                      class="size-3 text-n-slate-10"
                    />
                    <span class="text-[11px] text-n-slate-11">
                      {{ t('PIPELINE.AGENDA.QUIETEST_DAY') }}
                    </span>
                    <span
                      class="text-[11px] font-semibold text-n-slate-12 tabular-nums"
                    >
                      {{
                        t('PIPELINE.AGENDA.DAY_WITH_COUNT', {
                          day: quietestDay.day,
                          count: quietestDay.count,
                        })
                      }}
                    </span>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>

        <!-- Seção de Itens Recentes -->
        <div
          class="border-t border-n-weak mt-auto relative z-[3] w-full flex-shrink-0"
        >
          <div class="flex items-center justify-between px-5 py-2">
            <!-- Left: Toggle + Title + Count -->
            <button
              type="button"
              class="flex min-h-11 items-center gap-1.5 rounded-md px-2 -ml-2 hover:bg-n-alpha-1 focus-visible:outline-2 focus-visible:outline-n-brand transition-colors"
              :aria-expanded="isRecentItemsExpanded"
              aria-controls="pipeline-agenda-recent-items"
              @click="toggleRecentItems"
            >
              <fluent-icon
                :icon="isRecentItemsExpanded ? 'chevron-down' : 'chevron-right'"
                size="12"
                class="text-n-slate-9 flex-shrink-0"
              />
              <span class="text-[11px] font-semibold text-n-slate-12">
                {{ t('PIPELINE.AGENDA.RECENT_ITEMS') }}
              </span>
              <span
                class="text-[10px] text-n-slate-10 tabular-nums bg-n-alpha-1 px-1.5 py-0.5 rounded"
              >
                {{ filteredRecentItems.length }}/{{ recentItems.length }}
              </span>
            </button>

            <!-- Right: Search + Hint -->
            <div
              v-if="isRecentItemsExpanded"
              class="flex items-center gap-2"
              @click.stop
            >
              <!-- Drag hint -->
              <div
                class="hidden md:flex items-center gap-1.5 text-[10px] text-n-slate-10"
              >
                <fluent-icon icon="drag" size="10" class="flex-shrink-0" />
                <span>{{ t('PIPELINE.AGENDA.DRAG_TO_SCHEDULE') }}</span>
              </div>

              <div class="w-px h-3.5 bg-n-weak hidden md:block" />

              <!-- Expandable Search -->
              <div class="flex items-center">
                <div
                  class="flex items-center overflow-hidden transition-all duration-200 ease-out"
                  :class="showRecentSearch ? 'w-40' : 'w-0'"
                >
                  <div
                    class="h-[26px] w-full rounded-md border border-n-weak bg-n-alpha-1 flex items-center overflow-hidden transition-colors focus-within:border-n-blue-7 focus-within:bg-n-solid-2"
                  >
                    <Icon
                      icon="i-lucide-search"
                      class="size-3 text-n-slate-9 ml-2 flex-shrink-0"
                    />
                    <input
                      id="recent-items-search"
                      v-model="recentItemsSearchQuery"
                      type="search"
                      :aria-label="t('PIPELINE.AGENDA.SEARCH_ITEMS')"
                      class="w-full bg-transparent border-none outline-none ring-0 m-0 px-2 text-[11px] text-n-slate-12 placeholder:text-n-slate-9 reset-base no-margin"
                      :placeholder="t('PIPELINE.AGENDA.SEARCH_ITEMS')"
                      @blur="
                        !recentItemsSearchQuery && (showRecentSearch = false)
                      "
                    />
                  </div>
                </div>
                <button
                  type="button"
                  class="flex min-h-11 min-w-11 items-center justify-center rounded-md hover:bg-n-alpha-2 text-n-slate-9 hover:text-n-slate-12 focus-visible:outline-2 focus-visible:outline-n-brand transition-colors flex-shrink-0"
                  :aria-label="t('PIPELINE.AGENDA.SEARCH_ITEMS')"
                  @click="handleRecentSearch"
                >
                  <Icon icon="i-lucide-search" class="size-5" />
                </button>
              </div>
            </div>
          </div>

          <!-- Cards Container com transição -->
          <transition
            enter-active-class="transition-[opacity,max-height] duration-200 ease-out motion-reduce:transition-none"
            leave-active-class="transition-[opacity,max-height] duration-200 ease-in motion-reduce:transition-none"
            enter-from-class="opacity-0 max-h-0"
            enter-to-class="opacity-100 max-h-[400px]"
            leave-from-class="opacity-100 max-h-[400px]"
            leave-to-class="opacity-0 max-h-0"
          >
            <div
              v-show="isRecentItemsExpanded"
              id="pipeline-agenda-recent-items"
              class="overflow-hidden"
            >
              <!-- Empty state quando não há itens -->
              <div
                v-if="filteredRecentItems.length === 0"
                class="flex flex-col items-center justify-center py-8 text-n-slate-10"
              >
                <Icon
                  icon="i-lucide-search"
                  class="mb-2 size-8 text-n-slate-9"
                />
                <p class="text-sm">
                  {{
                    recentItemsSearchQuery
                      ? t('PIPELINE.AGENDA.NO_SEARCH_RESULTS')
                      : t('PIPELINE.AGENDA.NO_RECENT_ITEMS')
                  }}
                </p>
              </div>
              <div v-else class="max-h-64 overflow-y-auto px-4 pb-3">
                <div class="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:flex">
                  <button
                    v-for="item in filteredRecentItems"
                    :key="item.id"
                    type="button"
                    class="w-full px-3 py-2 text-left bg-n-solid-2 rounded-lg border border-n-weak cursor-grab hover:border-n-blue-7 hover:shadow-sm focus-visible:outline-2 focus-visible:outline-n-brand transition-[border-color,box-shadow] lg:w-72 lg:flex-shrink-0"
                    draggable="true"
                    @dragstart="
                      e => {
                        e.dataTransfer.setData('itemId', item.id);
                        e.dataTransfer.effectAllowed = 'move';
                      }
                    "
                    @click="handleCardDrop(item.id, selectedDate)"
                  >
                    <div class="flex items-center gap-2">
                      <!-- Priority Bar -->
                      <div
                        class="w-0.5 h-7 rounded-full flex-shrink-0"
                        :class="{
                          'bg-n-ruby-9': item.priority === 'urgent',
                          'bg-n-orange-9': item.priority === 'high',
                          'bg-n-amber-9': item.priority === 'medium',
                          'bg-n-green-9': item.priority === 'low',
                          'bg-n-slate-6':
                            !item.priority || item.priority === 'none',
                        }"
                      />

                      <!-- Conteúdo Principal -->
                      <div class="flex-1 min-w-0">
                        <div class="flex items-center gap-2">
                          <h4
                            class="text-[11px] font-medium text-n-slate-12 truncate flex-1"
                          >
                            {{ item.title }}
                          </h4>
                          <span
                            class="flex-shrink-0 rounded-md border px-1.5 py-px text-[9px] font-medium"
                            :class="getStageColorClasses(item.stageColor).badge"
                            :style="stageColorStyle(item.stageColor, 'badge')"
                          >
                            {{ item.stageName }}
                          </span>
                        </div>
                        <span class="text-[10px] text-n-slate-10">
                          {{ formatShortDate(item.created_at) }}
                        </span>
                      </div>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </transition>
        </div>
      </div>

      <!-- Substituir o Event Details Modal pelo PipelineCardDetails -->
      <Modal
        v-if="selectedItem && showItemDetails"
        v-model:show="showItemDetails"
        size="large"
        @close="handleCloseDetails"
      >
        <PipelineCardDetails
          v-if="selectedItem"
          :item="selectedItem"
          @close="handleCloseDetails"
          @edit="handleItemEdit"
          @item-updated="handleDetailItemUpdated"
        />
      </Modal>

      <!-- Add Event Modal -->
      <Modal
        v-model:show="showAddModal"
        :before-close="requestAddModalClose"
        @close="() => (showAddModal = false)"
      >
        <div class="flex flex-col max-h-[85vh] overflow-hidden">
          <div class="px-5 pt-5 pb-3">
            <h3 class="text-sm font-semibold text-n-slate-12">
              {{ t('PIPELINE.ADD_ITEM') }}
            </h3>
          </div>
          <PipelineCardForm
            v-if="store.getters['pipelines/getSelectedPipeline']"
            ref="addCardFormRef"
            :pipeline-id="store.getters['pipelines/getSelectedPipeline'].id"
            :stage="agendaDefaultStage"
            :initial-date="toLocalDateString(selectedDate)"
            @saved="handleItemCreated"
            @close="showAddModal = false"
          />
        </div>
      </Modal>

      <!-- Edit Modal -->
      <Modal
        v-model:show="showEditModal"
        :before-close="requestEditModalClose"
        @close="() => (showEditModal = false)"
      >
        <div class="flex flex-col max-h-[85vh] overflow-hidden">
          <div class="px-5 pt-5 pb-3">
            <h3 class="text-sm font-semibold text-n-slate-12">
              {{ t('PIPELINE.FORM.EDIT_ITEM') }}
            </h3>
          </div>
          <PipelineCardForm
            v-if="selectedItemToEdit"
            ref="editCardFormRef"
            :pipeline-id="store.getters['pipelines/getSelectedPipeline'].id"
            :stage="selectedItemToEdit.pipeline_stage"
            :initial-data="selectedItemToEdit"
            is-editing
            @saved="handleItemUpdated"
            @close="showEditModal = false"
          />
        </div>
      </Modal>

      <!-- Modal de Seleção de Hora -->
      <Modal
        v-model:show="showTimePickerModal"
        size="small"
        @close="cancelSchedule"
      >
        <div class="p-6">
          <h3 class="text-lg font-medium mb-4 text-n-slate-12">
            {{ t('PIPELINE.AGENDA.SELECT_TIME') }}
          </h3>

          <div class="space-y-4">
            <!-- Data selecionada (somente leitura) -->
            <div class="space-y-1.5">
              <p
                class="block text-xs font-semibold text-n-slate-11 uppercase tracking-wider"
              >
                {{ t('PIPELINE.AGENDA.SELECTED_DATE') }}
              </p>
              <div
                class="w-full bg-n-alpha-2 border border-n-weak rounded-lg px-3 py-2.5 text-sm text-n-slate-12"
              >
                {{ pendingScheduleDateLabel }}
              </div>
            </div>

            <!-- Seleção de hora -->
            <div class="space-y-1.5">
              <label
                for="pipeline-agenda-time"
                class="block text-xs font-semibold text-n-slate-11 uppercase tracking-wider"
              >
                {{ t('PIPELINE.AGENDA.SELECT_TIME_LABEL') }}
              </label>
              <Input
                id="pipeline-agenda-time"
                v-model="selectedTime"
                type="time"
                :aria-label="t('PIPELINE.AGENDA.SELECT_TIME_LABEL')"
                custom-input-class="!h-11"
              />
            </div>

            <!-- Opções rápidas de hora -->
            <fieldset class="space-y-1.5">
              <legend
                class="block text-xs font-semibold text-n-slate-11 uppercase tracking-wider"
              >
                {{ t('PIPELINE.AGENDA.QUICK_TIMES') }}
              </legend>
              <div class="flex flex-wrap gap-2">
                <button
                  v-for="time in [
                    '08:00',
                    '09:00',
                    '10:00',
                    '14:00',
                    '15:00',
                    '16:00',
                    '18:00',
                  ]"
                  :key="time"
                  type="button"
                  :aria-pressed="selectedTime === time"
                  class="min-h-11 px-3 py-2 text-xs font-medium rounded-lg focus-visible:outline-2 focus-visible:outline-n-brand transition-colors border"
                  :class="
                    selectedTime === time
                      ? 'bg-n-blue-9 text-white border-n-blue-10 shadow-sm'
                      : 'bg-n-solid-2 border-n-weak text-n-slate-11 hover:bg-n-alpha-2'
                  "
                  @click="selectedTime = time"
                >
                  {{ time }}
                </button>
              </div>
            </fieldset>
          </div>

          <!-- Botões de ação -->
          <div class="flex justify-end gap-3 mt-6 pt-4 border-t border-n-weak">
            <Button
              type="button"
              variant="ghost"
              color="slate"
              size="md"
              :disabled="isScheduling"
              @click="cancelSchedule"
            >
              {{ t('PIPELINE.CANCEL') }}
            </Button>
            <Button
              type="button"
              variant="solid"
              color="blue"
              size="md"
              :is-loading="isScheduling"
              :disabled="isScheduling || !selectedTime"
              @click="confirmScheduleWithTime"
            >
              {{ t('PIPELINE.AGENDA.CONFIRM_SCHEDULE') }}
            </Button>
          </div>
        </div>
      </Modal>

      <!-- Modal de Agendamento por Clique no Dia -->
      <Modal
        v-model:show="showScheduleModal"
        size="medium"
        @close="cancelScheduleModal"
      >
        <div class="p-6">
          <div class="flex items-center justify-between mb-4">
            <h3 class="text-lg font-medium text-n-slate-12">
              {{ t('PIPELINE.AGENDA.SCHEDULE_ITEM') }}
            </h3>
            <div
              v-if="scheduleModalDate"
              class="px-3 py-1.5 bg-n-blue-3 text-n-blue-11 rounded-lg text-sm font-medium"
            >
              {{
                new Intl.DateTimeFormat(getUserLocale(), {
                  weekday: 'short',
                  day: '2-digit',
                  month: 'short',
                }).format(scheduleModalDate)
              }}
            </div>
          </div>

          <!-- Campo de busca -->
          <div class="relative mb-4">
            <Icon
              icon="i-lucide-search"
              class="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-n-slate-9"
            />
            <input
              v-model="scheduleModalSearchQuery"
              type="text"
              :placeholder="t('PIPELINE.AGENDA.SEARCH_ITEMS_TO_SCHEDULE')"
              :aria-label="t('PIPELINE.AGENDA.SEARCH_ITEMS_TO_SCHEDULE')"
              class="min-h-11 w-full pl-10 pr-11 py-2.5 text-sm border border-n-weak rounded-lg bg-n-solid-2 text-n-slate-12 placeholder-n-slate-9 focus:ring-2 focus:ring-n-blue-6 focus:border-n-blue-7 transition-colors"
            />
            <button
              v-if="scheduleModalSearchQuery"
              type="button"
              :aria-label="t('PIPELINE.FORM.CLEAR')"
              class="absolute right-1 top-1/2 flex min-h-11 min-w-11 -translate-y-1/2 items-center justify-center rounded-lg text-n-slate-9 hover:text-n-slate-11 focus-visible:outline-2 focus-visible:outline-n-brand"
              @click="scheduleModalSearchQuery = ''"
            >
              <Icon icon="i-lucide-x" class="size-3.5" />
            </button>
          </div>

          <!-- Contagem de itens -->
          <div class="flex items-center justify-between mb-3">
            <span class="text-xs text-n-slate-10">
              {{
                `${t('PIPELINE.AGENDA.UNSCHEDULED_ITEMS')}: ${allUnscheduledItems.length}`
              }}
            </span>
            <span
              v-if="scheduleModalSearchQuery"
              class="text-xs text-n-slate-10"
            >
              {{
                `${t('PIPELINE.AGENDA.SHOWING')}: ${filteredScheduleModalItems.length}`
              }}
            </span>
          </div>

          <!-- Lista de itens -->
          <div
            role="group"
            :aria-label="t('PIPELINE.AGENDA.UNSCHEDULED_ITEMS')"
            class="max-h-[400px] overflow-y-auto space-y-2 mb-4"
          >
            <div
              v-if="filteredScheduleModalItems.length === 0"
              class="flex flex-col items-center justify-center py-12 text-n-slate-10"
            >
              <Icon
                icon="i-lucide-search"
                class="mb-3 size-12 text-n-slate-7"
              />
              <p class="text-sm">
                {{
                  scheduleModalSearchQuery
                    ? t('PIPELINE.AGENDA.NO_SEARCH_RESULTS')
                    : t('PIPELINE.AGENDA.NO_UNSCHEDULED_ITEMS')
                }}
              </p>
            </div>
            <button
              v-for="item in filteredScheduleModalItems"
              :key="item.id"
              type="button"
              :aria-pressed="selectedItemToSchedule?.id === item.id"
              class="w-full p-3 text-left rounded-lg border cursor-pointer focus-visible:outline-2 focus-visible:outline-n-brand transition-[border-color,background-color,box-shadow]"
              :class="{
                'border-n-blue-9 bg-n-blue-3 ring-2 ring-n-blue-6':
                  selectedItemToSchedule?.id === item.id,
                'border-n-weak hover:border-n-slate-7 bg-n-solid-2':
                  selectedItemToSchedule?.id !== item.id,
              }"
              @click="selectItemForSchedule(item)"
            >
              <div class="flex items-start gap-3">
                <!-- Selected item indicator -->
                <div
                  class="w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5"
                  :class="{
                    'border-n-blue-9 bg-n-blue-9':
                      selectedItemToSchedule?.id === item.id,
                    'border-n-slate-7': selectedItemToSchedule?.id !== item.id,
                  }"
                >
                  <Icon
                    v-if="selectedItemToSchedule?.id === item.id"
                    icon="i-lucide-check"
                    class="size-3 text-white"
                  />
                </div>

                <!-- Item content -->
                <div class="flex-1 min-w-0">
                  <div class="flex items-center gap-2 mb-1">
                    <h4 class="text-sm font-medium text-n-slate-12 truncate">
                      {{ item.title || t('PIPELINE.UNTITLED_ITEM') }}
                    </h4>
                    <span
                      class="flex-shrink-0 rounded-full border px-2 py-0.5 text-xs"
                      :class="getStageColorClasses(item.stageColor).badge"
                      :style="stageColorStyle(item.stageColor, 'badge')"
                    >
                      {{ item.stageName || t('PIPELINE.NO_STAGE') }}
                    </span>
                  </div>
                  <div class="flex items-center gap-3 text-xs text-n-slate-10">
                    <span class="flex items-center gap-1">
                      <Icon icon="i-lucide-calendar" class="size-3" />
                      {{ formatShortDate(item.created_at) }}
                    </span>
                    <span
                      v-if="item.priority && item.priority !== 'none'"
                      class="flex items-center gap-1"
                      :class="getPriorityTextClass(item.priority)"
                    >
                      <Icon icon="i-lucide-flag" class="size-3" />
                      {{
                        t(`PIPELINE.PRIORITIES.${item.priority.toUpperCase()}`)
                      }}
                    </span>
                  </div>
                </div>
              </div>
            </button>
          </div>

          <!-- Botões de ação -->
          <div
            class="flex flex-col items-stretch justify-between gap-3 pt-4 border-t border-n-weak sm:flex-row sm:items-center"
          >
            <Button
              type="button"
              variant="ghost"
              color="slate"
              size="sm"
              class="min-h-11"
              @click="
                showAddModal = true;
                showScheduleModal = false;
              "
            >
              <Icon icon="i-lucide-plus" class="size-3.5 mr-1" />
              {{ t('PIPELINE.AGENDA.CREATE_NEW') }}
            </Button>
            <div class="flex justify-end gap-3">
              <Button
                type="button"
                variant="ghost"
                color="slate"
                size="md"
                class="min-h-11"
                @click="cancelScheduleModal"
              >
                {{ t('PIPELINE.CANCEL') }}
              </Button>
              <Button
                type="button"
                variant="solid"
                color="blue"
                size="md"
                class="min-h-11"
                :disabled="!selectedItemToSchedule"
                @click="confirmScheduleFromModal"
              >
                {{ t('PIPELINE.AGENDA.SELECT_AND_SCHEDULE') }}
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    </template>
  </div>
</template>
